'use es6';

import { defer } from 'laboratory-lib/core/helpers';
const DEFAULT_STATUS = {
  isActive: false,
  isEnrolled: false,
  isDefaulted: true,
  isCached: false
};
const CACHED_STATUS = {
  isActive: false,
  isEnrolled: true,
  isDefaulted: false,
  isCached: true
};
export function adaptParametersFromApi(parameters = []) {
  return parameters.reduce((acc, parameter) => {
    acc[parameter.key] = parameter.value;
    return acc;
  }, {});
}
export function adaptTreatmentsFromApi(treatments = []) {
  return treatments.reduce((acc, treatment) => {
    acc[treatment.key] = Object.assign({}, treatment, {
      parameters: adaptParametersFromApi(treatment.parameters || [])
    });
    return acc;
  }, {});
}
export function hasValidStatus(treatment = {}) {
  return !!(treatment && treatment.status && typeof treatment.status.isActive === 'boolean' && typeof treatment.status.isEnrolled === 'boolean' && typeof treatment.status.isDefaulted === 'boolean');
}
export function canExpose(treatment = {}) {
  return !!(treatment && treatment.key && treatment.identifier);
}
export function shouldExpose(treatment = {}) {
  if (!canExpose(treatment)) return false;
  if (!treatment.status) return true;
  return !treatment.status.isDefaulted;
}
export function shouldUseOption(options = [], value) {
  return value && options.indexOf(value) !== -1;
}
export function resolveParameter(options = [], values = []) {
  return values.find(({
    value
  }) => shouldUseOption(options, value)) || values[values.length - 1];
}
export function resolveParameters(treatmentKey, parameters = {}, schema = {}, overridesFromCache = {}, overridesFromSearch = {}) {
  let statusOverride;
  const resolvedParameters = Object.keys(schema).reduce((acc, paramKey) => {
    const options = schema[paramKey];
    const cacheKey = `${treatmentKey}-${paramKey}`;
    const overrideFromSearch = overridesFromSearch[cacheKey];
    const overrideFromCache = overridesFromCache[cacheKey];
    const valueFromNetwork = parameters[paramKey];
    const values = [{
      value: overrideFromSearch,
      status: null
    }, {
      value: overrideFromCache,
      status: CACHED_STATUS
    }, {
      value: valueFromNetwork,
      status: null
    }, {
      value: options[0],
      status: DEFAULT_STATUS
    }];
    const {
      value,
      status
    } = resolveParameter(options, values);

    if (status) {
      statusOverride = status;
    }

    acc[paramKey] = value;
    return acc;
  }, parameters);
  return {
    resolvedParameters,
    statusOverride
  };
}
export function resolveTreatment(treatment, definition = {}, overridesFromSearch = {}, overridesFromCache = {}) {
  const parameters = treatment ? treatment.parameters || {} : {};
  const status = hasValidStatus(treatment) ? treatment.status : DEFAULT_STATUS;
  const resolved = Object.assign({}, treatment || definition);
  const {
    resolvedParameters,
    statusOverride
  } = resolveParameters(definition.key, parameters, definition.parameters, overridesFromCache, overridesFromSearch);
  resolved.parameters = resolvedParameters;
  resolved.status = statusOverride || status;

  if (typeof resolved.status.isCached === 'undefined') {
    resolved.status.isCached = false;
  }

  if (typeof definition.maxAge === 'number') {
    resolved.maxAge = definition.maxAge;
  }

  return resolved;
}
export function resolveTreatments({
  treatments = [],
  schema = {},
  overridesFromCache = {},
  overridesFromSearch = {}
} = {}) {
  const adaptedTreatments = adaptTreatmentsFromApi(treatments);
  return Object.keys(schema).reduce((acc, key) => {
    const definition = schema[key];
    definition.key = key;
    const treatment = adaptedTreatments[key];
    acc[key] = resolveTreatment(treatment, definition, overridesFromSearch, overridesFromCache);
    return acc;
  }, adaptedTreatments);
}
export function resolveExposure(treatmentOrKey, treatments = {}) {
  const keyMode = typeof treatmentOrKey === 'string';
  const treatment = keyMode ? treatments[treatmentOrKey] : treatmentOrKey;
  if (!shouldExpose(treatment)) return null;
  return {
    key: treatment.key,
    identifier: treatment.identifier
  };
}
export function resolveExposures({
  treatments = {},
  treatmentOrKeys = []
} = {}) {
  return treatmentOrKeys.map(treatmentOrKey => resolveExposure(treatmentOrKey, treatments)).filter(exposure => !!exposure);
}
export const createDeferredCallstack = fnHead => {
  let callstack = fnHead;

  const deferExecution = data => {
    defer(() => {
      if (typeof callstack === 'function') {
        const ref = callstack;
        callstack = undefined;
        ref(data);
      }
    });
  };

  return {
    execute: deferExecution,
    defer: fnTail => {
      const ref = callstack;

      callstack = data => {
        if (typeof ref === 'function') ref(data);
        fnTail(data);
      };
    }
  };
};
export function createAsyncResolver({
  getAsyncData,
  resolve,
  onError
} = {}) {
  let cache;
  let callstack;
  return callback => {
    if (callstack) {
      callstack.defer(callback);

      if (cache) {
        callstack.execute(resolve(cache));
      }
    } else {
      callstack = createDeferredCallstack(callback);
      getAsyncData({
        onComplete: data => {
          cache = data || {};
          callstack.execute(resolve(cache));
        },
        onError: (err, data) => {
          if (typeof onError === 'function') {
            onError(err);
          }

          cache = data;
          callstack.execute(resolve(cache));
        }
      });
    }
  };
}
export function deserializeCopyVariant(serialized) {
  const parts = serialized.split(':::');
  const key = parts[1];
  const variant = parts[2].split('__')[0];
  return [key, variant];
}
export function isCopyVariant(key) {
  return key.indexOf('__copym:::') !== -1;
}
export function isSwappableCopyVariant(key, treatments) {
  const [treatmentKey, variant] = deserializeCopyVariant(key);
  const treatment = treatments[treatmentKey];
  return treatment && treatment.parameters && treatment.parameters['group'] === variant;
}
export function findSwappableCopy({
  translations = {},
  treatments = {},
  onError
} = {}) {
  try {
    const flatTranslations = [translations, translations['translate-copy'], translations['do-not-translate-copy']].filter(branch => !!branch).reduce((acc, branch) => Object.assign({}, acc, {}, branch), {});
    const copyVariants = Object.keys(flatTranslations).filter(isCopyVariant);
    return copyVariants.reduce((acc, key) => {
      if (isSwappableCopyVariant(key, treatments)) {
        acc[key] = flatTranslations[key];
      }

      return acc;
    }, {});
  } catch (err) {
    defer(() => {
      if (typeof onError === 'function') {
        onError(err);
      }
    });
    return {};
  }
}